import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Divider,
  IconButton,
  Box,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomTypography from "../../globalStyles/fonts";
import { formatDateTime } from "../../utils/formatDate";

const InventoryConfigDetailsModal = ({ config, open, onClose }) => {
  if (!config) return null;

  const DetailRow = ({ label, value, chipVariant = false }) => (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12} md={4}>
        <CustomTypography
          style={{
            fontSize: 14,
            fontFamily: "Poppins",
            fontWeight: 500,
            color: "var(--text-tertiary)",
            wordWrap: "break-word",
            whiteSpace: "normal",
          }}
        >
          {label}
        </CustomTypography>
      </Grid>
      <Grid item xs={12} md={8}>
        {chipVariant ? (
          <Chip
            label={value ? "Active" : "Inactive"}
            size="small"
            sx={{
              backgroundColor: value ? "#D7E9FF" : "#FFDEDF",
              color: value ? "#0070FF" : "#D13438",
              fontWeight: 500,
              fontSize: "12px",
            }}
          />
        ) : (
          <CustomTypography
            style={{
              fontSize: 14,
              fontFamily: "Poppins",
              fontWeight: 400,
              color: "#667085",
              wordWrap: "break-word",
              whiteSpace: "normal",
              lineHeight: 1.5,
            }}
          >
            {value || "N/A"}
          </CustomTypography>
        )}
      </Grid>
    </Grid>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="inventory-config-details-title"
      sx={{
        "& .MuiDialog-paper": {
          wordWrap: "break-word",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <DialogTitle
          id="inventory-config-details-title"
          sx={{
            fontFamily: "Poppins",
            fontSize: 24,
            fontWeight: 500,
            color: "var(--text-primary)",
            wordWrap: "break-word",
            whiteSpace: "normal",
          }}
        >
          Inventory/Supplier Configuration Details
        </DialogTitle>
        <IconButton onClick={onClose} sx={{ mr: 2 }} aria-label="close">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <DialogContent sx={{ wordWrap: "break-word", overflow: "auto" }}>
        <Grid container spacing={3}>
          {/* Product Information Section */}
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
                color: "var(--text-tertiary)",
                mb: 2,
                wordWrap: "break-word",
                whiteSpace: "normal",
              }}
            >
              Product Information
            </Typography>
            <Box sx={{ pl: 1 }}>
              <DetailRow label="Product Name" value={config?.product?.name} />
              <DetailRow label="SKU" value={config?.sku} />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* Supplier & Warehouse Section */}
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
                color: "var(--text-tertiary)",
                mb: 2,
                wordWrap: "break-word",
                whiteSpace: "normal",
              }}
            >
              Supplier & Warehouse
            </Typography>
            <Box sx={{ pl: 1 }}>
              <DetailRow label="Supplier" value={config?.supplier?.name} />
              <DetailRow
                label="Warehouse"
                value={config?.warehouse?.provider?.name}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* Inventory Configuration Section */}
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
                color: "var(--text-tertiary)",
                mb: 2,
                wordWrap: "break-word",
                whiteSpace: "normal",
              }}
            >
              Inventory Configuration
            </Typography>
            <Box sx={{ pl: 1 }}>
              <DetailRow
                label="Safety Stock Count"
                value={config?.safety_stock_count?.toString()}
              />
              <DetailRow
                label="Transportation & Inbound Receiving"
                value={
                  config?.avg_fullfillment_days
                    ? `${config.avg_fullfillment_days} days`
                    : "N/A"
                }
              />
              <DetailRow
                label="Supplier Fulfillment Days"
                value={
                  config?.avg_fullfillment_days_supplier
                    ? `${config.avg_fullfillment_days_supplier} days`
                    : "N/A"
                }
              />
              <DetailRow
                label="Minimum Order Quantity"
                value={config?.min_order_quantity?.toString()}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* Status & Metadata Section */}
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
                color: "var(--text-tertiary)",
                mb: 2,
                wordWrap: "break-word",
                whiteSpace: "normal",
              }}
            >
              Status & Information
            </Typography>
            <Box sx={{ pl: 1 }}>
              <DetailRow
                label="Status"
                value={config?.is_active}
                chipVariant={true}
              />
              <DetailRow
                label="Created By"
                value={
                  config?.created_by?.name
                    ? `${config?.created_by?.name}`.trim()
                    : "N/A"
                }
              />
              <DetailRow
                label="Updated By"
                value={
                  config?.updated_by?.name
                    ? `${config?.updated_by?.name}`.trim()
                    : "N/A"
                }
              />
              <DetailRow
                label="Created At"
                value={
                  config?.createdAt
                    ? formatDateTime(config.createdAt)
                    : "N/A"
                }
              />
              <DetailRow
                label="Updated At"
                value={
                  config?.updatedAt
                    ? formatDateTime(config.updatedAt)
                    : "N/A"
                }
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            fontFamily: "Poppins",
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
            minWidth: 100,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InventoryConfigDetailsModal;
